import { defineAsyncComponent, markRaw, reactive, type Component } from 'vue'
import type { ConfirmModalOptions, ModalStackItem, ModalStore, PromptModalOptions, Size } from './types'
import ConfirmModal from '../../ui/modal/ConfirmModal.vue'
import AlertModal from '../../ui/modal/AlertModal.vue'
import IFrameModal from '../../ui/modal/IFrameModal.vue'
import PromptModal from '../../ui/modal/PromptModal.vue'
import PDFModal from '../../ui/modal/PDFModal.vue'
import NotesModal from './NotesModal.vue'
import type { EmptyCallback, ParamsCallback } from '../../utils/global-types'
import { defer, getRandomUUID } from '../../utils/helpers'

const stack = reactive([] as ModalStackItem[])

export function useModalStore(): ModalStore {
    function open(component: () => Promise<any>, props = {}, size?: Size, disableEscape?: boolean) {
        const loader = markRaw(defineAsyncComponent({ loader: component }));

        if (!loader) {
            console.error("Modal not found");
            return;
        }

        return openRaw(loader, props, size, disableEscape)
    }

    function openRaw(component: Component, props = {}, size?: Size, disableEscape?: boolean) {
        const id = getRandomUUID();
        let cancelDef = defer(),
            closeDef = defer(),
            done = false;

        //is this overkill?
        const settle = (type: 'close' | 'cancel', val?: any) => {
            if (done) return;
            done = true;

            if (type == 'close') {
                closeDef.resolve(val);
                cancelDef = null;
                return;
            }

            //cancel
            cancelDef.resolve(val);
            closeDef = null;
        }

        stack.push({
            id,
            component,
            disableEscape,
            size,
            props: {
                ...props,
                close: (val: any) => close(val, id)
            },
            resolver: val => settle('close', val),
            cancelResolver: val => settle('cancel', val)
        });

        updateBodyClass();

        return {
            result: closeDef.promise,
            canceled: cancelDef.promise,
        }
    }

    function close(val?: any, id?: string) {
        if (id) {
            const index = stack.findIndex(x => x.id == id);
            stack[index]?.resolver?.(val);
            stack.splice(index, 1);
            updateBodyClass();
            return;
        }

        const top = stack.pop()
        updateBodyClass()
        top?.resolver?.(val);
    }

    function cancel(val?: any) {
        const top = stack.pop()
        updateBodyClass();
        top?.cancelResolver?.(val);
    }

    function top() {
        return stack[stack.length - 1] || {}
    }

    function dismissAll() {
        while (stack.length > 0) {
            stack.pop()
        }
        updateBodyClass()
    }

    function confirm(message: string, callback: ((val: boolean) => void), options?: ConfirmModalOptions) {
        options = Object.assign({
            closeButton: true,
            buttons: {
                confirm: { label: 'Yes', className: 'btn-primary' },
                cancel: { label: 'No', className: 'btn-default' }
            }
        }, options || {})

        const component = markRaw(ConfirmModal)
        openRaw(component, {
            message,
            options: options,
            callback,
            close: (val: any) => close(val)
        })
    }

    function alert(message: string, callback?: EmptyCallback) {
        const component = markRaw(AlertModal)
        openRaw(component, {
            message,
            callback,
        })
    }

    function prompt(callback: EmptyCallback | ParamsCallback, options?: PromptModalOptions) {
        const component = markRaw(PromptModal)
        openRaw(component, {
            options: Object.assign({
                closeButton: true,
                buttons: {
                    confirm: { label: 'Yes', className: 'btn-primary' },
                    cancel: { label: 'No', className: 'btn-default' }
                }
            }, options || {}),
            callback,
            close: (val: any) => close(val)
        })
    }

    function iframe(src: string, options?: any) {
        const component = markRaw(IFrameModal)
        const result = openRaw(component, {
            source: src,
            options,
            close: (val: any) => close(val)
        }, options?.size)

        return result
    }

    function pdfPreview(blob: Blob, filename: string, title: string, callback?: EmptyCallback, height: number | null = null) {
        const component = markRaw(PDFModal)
        openRaw(component, {
            blob,
            title,
            filename,
            height,
            callback,
            close: (val: any) => close(val)
        }, 'lg')
    }

    function editNotes(title: string, notes: string, limit?: number) {
        const component = markRaw(NotesModal)
        return openRaw(component, {
            title,
            notes,
            limit,
            close: (val: any) => close(val)
        })
    }

    return {
        stack,
        open,
        cancel,
        close,
        top,
        confirm,
        alert,
        iframe,
        prompt,
        pdfPreview,
        editNotes,
        dismissAll
    }
}

function updateBodyClass() {
    if (stack.length > 0) {
        document.body.classList.add('modal-open')
        return;
    }
    document.body.classList.remove('modal-open')
}